// =============================================================================
// Contract pricing — the "Contract" tab on the customer page.
//
// One question brings anyone to this tab: DOES THIS HOSPITAL'S NEXT QUOTE GET
// CONTRACT PRICING, YES OR NO? So that answer is the first thing on the screen,
// and it is driven by the server's `isLiveNow` — never re-derived in the
// browser, because the server owns "Active + enabled + inside the window".
//
// Backend (built, live, unchanged by this file):
//   GET    /api/customers/{custNum}/price-contract       -> { custNum, governing: Dto|null, attached: Dto[] }
//   GET    /api/price-contracts?status=&page=&pageSize=  -> { items: Dto[], totalCount }   (the Attach picker)
//   POST   /api/price-contracts/{id}/customers           -> Dto   body {custNum, custId, customerName}
//   DELETE /api/price-contracts/{id}/customers/{custNum} -> Dto
//
// Server rules this file must not contradict (PriceContractController.cs,
// ContractAdminModels.cs, ContractAdminService.cs):
//
//  * RENDER WHAT COMES BACK. Every mutation returns the full post-write row —
//    a DB trigger rewrites Status/IsEnabled/approval columns underneath the
//    write — so attach/detach paint the returned Dto, then re-read the panel
//    for the one thing the row cannot tell us: which contract now GOVERNS this
//    customer. There is no 204 anywhere in this API, deliberately.
//  * GOVERNING **AND** ATTACHED, BOTH. `attached[]` is every active attachment
//    and it INCLUDES the governing one. A renewal attached in November while
//    this year's contract still runs is normal; hiding it makes the panel look
//    broken exactly when somebody is checking it.
//  * A 409 ON ATTACH CARRIES `conflictingContract`. Name it — "3PROSP772 is
//    already under SSC-0001 until 31 Dec" — never the word "conflict". The
//    server writes that sentence into `error`; humanError prefers it.
//  * ATTACH IS IDEMPOTENT and a re-attach revives a previously detached row.
//    Neither is special-cased here; an already-attached row is only labelled.
//  * Detach is a SOFT detach — the row is kept with RemovedAt/RemovedBy.
//
// rowVersion does NOT travel on these two calls: AttachCustomerRequest has no
// rowVersion field and the DELETE has no body. It is load-bearing on
// PUT/approve/enable/disable, which live on the /price-contracts admin page.
//
// Entitlements (SQL/056): 00082 view · 00083 manage (attach/detach) ·
// 00084 approve/enable/disable · 00085 import.
//
// NOTE ON THE FALLBACK ARG: every other hasAction() call in this SPA passes
// `true`, which keeps buttons alive for sessions created before entitlements
// shipped. That reasoning cannot apply here — a session predating this feature
// can never carry 00082 — so a `true` fallback would paint a tab the API
// answers with 403. These four ids default-deny on purpose.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import { apiFetch, hasAction } from "../utils/auth";
import { pickField } from "../utils/fieldHelpers";
import { humanError, errorText } from "../utils/apiError";
// The SAME create form the admin page uses. Shared rather than reimplemented so there is one
// copy of the signed-percent echo — see the note in that file.
import ContractFormModal from "./ContractFormModal";

const API_URL = process.env.REACT_APP_API_URL;

// Case-insensitive field read with a default — the house helper, same as
// CustomerDetail.js. The wire is camelCase; this survives either casing.
const f = (o, k, d = "") => { const v = pickField(o, k); return v === undefined || v === null ? d : v; };

// ── formatting ────────────────────────────────────────────────────────────────
// ContractStartDate/EndDate are DateOnly — "2026-01-01" on the wire. Feeding
// that to new Date() parses it as UTC midnight and renders 31 Dec across the
// US, which is precisely the wrong day for a contract window. Build the date
// from its parts instead.
const dayKey = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ""));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};
const fmtDay = (v) => {
  if (!v) return "—";
  const k = dayKey(v);
  if (!k) { const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString(); }
  const [y, mo, da] = k.split("-");
  return new Date(Number(y), Number(mo) - 1, Number(da)).toLocaleDateString();
};
const todayKey = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
// CreatedAt / ApprovedAt / DisabledAt are DateTime — date + time is wanted.
const fmtStamp = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? String(s) : d.toLocaleString(); };

// How a contract is named out loud: the ref if it has one, else the name.
const label = (c) => String(f(c, "contractRef") || f(c, "contractName") || `contract ${f(c, "id")}`);

// adjustPercent is SIGNED and "10%" alone cannot be read: -10 is cheaper,
// +10 is dearer. Always show the sign AND the word.
const pctSigned = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const trimmed = Number(n.toFixed(4));
  return `${trimmed > 0 ? "+" : ""}${trimmed}%`;
};
const pctWords = (v, year) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const book = year ? `the ${year} book` : "the reference book";
  if (n === 0) return `the same as ${book} — no adjustment`;
  const mag = Number(Math.abs(n).toFixed(4));
  return n < 0 ? `${mag}% LESS than ${book}` : `${mag}% MORE than ${book}`;
};

const statusPill = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "qw-pill-emerald";
  if (s === "pendingapproval") return "qw-pill-amber";
  if (s === "draft") return "qw-pill-slate";
  return "qw-pill-slate"; // Expired | Cancelled
};
const statusWord = (status) =>
  String(status || "").toLowerCase() === "pendingapproval" ? "PENDING APPROVAL" : String(status || "—").toUpperCase();

// A short, honest reason a contract is attached but isn't pricing anything.
// `isLiveNow` remains the authority for the yes/no; this is only the colour
// commentary next to it, ordered so it agrees with the server's own test.
// WHY a contract is not pricing today.
//
// The server answers this in `pricingModeDetail`, and it is the ONLY thing that can: contract
// pricing sits behind three runtime switches the browser cannot see — the master flag (which
// defaults OFF), shadow mode (which defaults ON and works prices out without writing them), and
// the plan gate. A contract can be approved, enabled and mid-window and still price nothing.
//
// Everything below the first line is a fallback for a server that predates those fields. It can
// only ever explain the contract's own paperwork, so it must not be preferred over the server's
// answer — that was the original bug here: the tab printed a confident green tick over a system
// that was switched off.
const whyNotLive = (c) => {
  const fromServer = f(c, "pricingModeDetail", "");
  if (fromServer) return String(fromServer).replace(/\.$/, "");

  if (f(c, "isEnabled", true) === false) return "it is switched off";
  const s = String(f(c, "status") || "").toLowerCase();
  if (s === "draft") return "it is still a draft — nobody has submitted it for approval";
  if (s === "pendingapproval") return "it is waiting to be approved";
  if (s === "cancelled") return "it was cancelled";
  const start = dayKey(f(c, "contractStartDate"));
  const end = dayKey(f(c, "contractEndDate"));
  const today = todayKey();
  if (start && today < start) return `it does not start until ${fmtDay(start)}`;
  if (end && today > end) return `its window ended on ${fmtDay(end)}`;
  if (s === "expired") return "its window has ended";
  return "it is not the contract pricing this customer today";
};

// The four states the server can report, and what each one actually means for a rep about to
// quote this hospital. The server computes pricingMode because the browser cannot: contract
// pricing sits behind runtime switches (a master flag, shadow mode, a plan gate) that no
// client-side derivation can see.
function PricingHeadline({ c, who }) {
  const mode = String(f(c, "pricingMode", "") || "");
  const detail = String(f(c, "pricingModeDetail", "") || "");
  const ready = f(c, "contractReady", false) === true;

  // Older server, or a field that did not come back: fall back to the binary rather than
  // render nothing. isLiveNow is still the honest answer, just a blunter one.
  if (!mode) {
    return f(c, "isLiveNow", false) === true ? (
      <Info tone="qw-info-emerald" style={{ fontWeight: 700 }}>
        ✔ This contract prices {who}'s quotes today.
      </Info>
    ) : (
      <Info tone="qw-info-amber" style={{ fontWeight: 700 }}>
        This contract does <u>NOT</u> price {who}'s quotes today — {whyNotLive(c)}.
        Their quotes fall back to catalogue pricing.
      </Info>
    );
  }

  if (mode === "Live")
    return (
      <Info tone="qw-info-emerald" style={{ fontWeight: 700 }}>
        ✔ This contract prices {who}'s quotes today.
        {detail && <div style={{ fontWeight: 500, marginTop: 4, fontSize: 12.5 }}>{detail}</div>}
      </Info>
    );

  // SHADOW MODE IS NOT A FAULT. Nothing is wrong with this contract — the engine is working
  // its prices out and recording them while quotes still go out at catalogue prices. Saying
  // so plainly matters: a rep who reads "live" during a shadow run will quote on it.
  if (mode === "ShadowMode")
    return (
      <Info tone="qw-info-indigo" style={{ fontWeight: 700 }}>
        This contract is approved and in date, but nothing is being charged on it yet.
        <div style={{ fontWeight: 500, marginTop: 4, fontSize: 12.5 }}>
          {detail || "Contract pricing is in shadow mode: prices are worked out and recorded, " +
                     "but quotes still go out at catalogue prices."}{" "}
          Quote {who} as normal — <strong>do not promise contract prices yet</strong>.
        </div>
      </Info>
    );

  // The contract is fine; the feature is off in this environment. Nobody should go looking
  // for a problem with the paperwork.
  if (mode === "SwitchedOff")
    return (
      <Info tone="qw-info-slate" style={{ fontWeight: 700 }}>
        Contract pricing is switched off here, so {who}'s quotes use catalogue prices.
        <div style={{ fontWeight: 500, marginTop: 4, fontSize: 12.5 }}>
          {detail || "This contract is approved and in date and takes effect the moment it is switched on."}
        </div>
      </Info>
    );

  // NotReady — this one IS about the contract, and is the only state with something to fix.
  return (
    <Info tone="qw-info-amber" style={{ fontWeight: 700 }}>
      This contract does <u>NOT</u> price {who}'s quotes today — {detail || whyNotLive(c)}
      <div style={{ fontWeight: 500, marginTop: 4, fontSize: 12.5 }}>
        Their quotes fall back to catalogue pricing until this is resolved.
        {ready === false && " Nothing else is wrong with the contract itself."}
      </div>
    </Info>
  );
}

const STATUS_OPTIONS = ["Draft", "PendingApproval", "Active", "Expired", "Cancelled"];
const PICKER_PAGE_SIZE = 25;

// A coloured callout. `.qw-info` is `display: flex` with a 12px gap and no
// wrapping — it was written for an icon beside a block of text — so anything
// with more than one child element lays out as COLUMNS and pushes the card
// sideways on a phone. One wrapper child keeps every callout a normal block.
function Info({ tone = "", style, children }) {
  return (
    <div className={("qw-info " + tone).trim()} style={style}>
      <div style={{ minWidth: 0, width: "100%", wordBreak: "break-word" }}>{children}</div>
    </div>
  );
}

// =============================================================================
// The tab
// =============================================================================
export default function CustomerContractTab({ custNum, custId, customerName }) {
  const navigate = useNavigate();

  // Default-deny on purpose — see the header note.
  const canView = hasAction("00082");
  const canManage = hasAction("00083");

  const [panel, setPanel] = useState(null);       // { custNum, governing, attached } | null
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);     // { kind: "ok" | "err", msg }
  const [acting, setActing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const custNumInt = Number(custNum);
  const who = String(customerName || custId || `customer ${custNum}`);

  const load = useCallback(async (quiet = false) => {
    if (!canView || !Number.isFinite(custNumInt)) { setLoading(false); return; }
    if (quiet) setRefreshing(true); else setLoading(true);
    if (!quiet) setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/customers/${custNumInt}/price-contract`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't load this customer's contract pricing."));
      setError(null);
      setPanel({
        custNum: f(d, "custNum", custNumInt),
        governing: f(d, "governing", null) || null,
        attached: f(d, "attached", []) || [],
      });
    } catch (e) {
      const msg = errorText(e, "Couldn't load this customer's contract pricing.");
      // A quiet reload only ever follows a mutation that already succeeded.
      // Blanking the panel then would hide the change the user just made, so
      // keep what is on screen and say plainly that it may be a step behind.
      if (quiet) setNotice({ kind: "err", msg: `The change went through, but this panel couldn't be refreshed: ${msg}` });
      else setError(msg);
    } finally {
      if (quiet) setRefreshing(false); else setLoading(false);
    }
  }, [custNumInt, canView]);

  useEffect(() => { load(); }, [load]);

  // Rule 1 — paint the row the server just handed back, before anything else.
  //
  // Membership comes out of that same row: a mutation response is the DETAIL
  // shape, so customers[] is filled, and whether THIS customer is still an
  // active attachment is a fact on it rather than a guess from what we sent.
  // What the row cannot answer is which contract now GOVERNS this customer —
  // isLiveNow is a property of the contract, not of this attachment — so a
  // newly attached contract is never promoted here. The re-read settles that.
  const applyRow = (row) => {
    const id = f(row, "id", null);
    if (id == null) return;
    const list = f(row, "customers", null);
    const stillOn = Array.isArray(list)
      ? list.some((m) => Number(f(m, "custNum")) === custNumInt && f(m, "isActive", false) === true)
      : null;   // shape unexpected — leave membership alone, let the re-read decide
    setPanel((p) => {
      if (!p) return p;
      const prev = p.attached || [];
      const had = prev.some((c) => f(c, "id") === id);
      let attached;
      if (stillOn === false) attached = prev.filter((c) => f(c, "id") !== id);
      else if (had) attached = prev.map((c) => (f(c, "id") === id ? row : c));
      else attached = stillOn === true ? [row, ...prev] : prev;

      let governing = p.governing;
      if (governing && f(governing, "id") === id) governing = stillOn === false ? null : row;
      return { ...p, attached, governing };
    });
  };

  // CREATE-AND-ATTACH — the day-one case.
  //
  // A hospital signs and no contract exists yet. Without this, the path is: leave the customer,
  // go to the admin page, create the contract, remember the CustNum, come back, attach. Five
  // steps across two screens to record one thing that happened.
  //
  // The contract is created and attached in one action. It is created as a DRAFT, exactly as the
  // admin page creates it — approval stays where it belongs, on the admin screen, with the
  // separate entitlement and the second person. This shortens the recording, not the control.
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const createAndAttach = async (payload) => {
    setCreating(true);
    try {
      const r = await apiFetch(`${API_URL}/api/price-contracts`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const body = await r.json().catch(() => ({}));      // ONE read, before any branch
      if (!r.ok) {
        setNotice({ kind: "err", msg: humanError(r, body, "Couldn't create the contract.") });
        return;
      }

      // Attach immediately. If THIS fails the contract still exists and is not lost — say so,
      // and point at where it can be picked up, rather than implying nothing happened.
      const res = await attach(body);
      if (res && res.error) {
        setNotice({
          kind: "err",
          msg: `${label(body)} was created, but ${who} could not be attached to it: ${res.error} ` +
               `The contract is on the contracts screen and nothing has been lost.`,
        });
      } else {
        setNotice({ kind: "ok", msg: `${label(body)} created and ${who} attached. It needs approving before it prices anything.` });
      }
      setCreateOpen(false);
      await load();
    } catch (e) {
      setNotice({ kind: "err", msg: errorText(e, "Couldn't create the contract.") });
    } finally {
      setCreating(false);
    }
  };

  // Attach. Returns { ok } or { error, conflicting } so the picker can show the
  // refusal next to the list the user is still looking at.
  const attach = async (contract) => {
    const id = f(contract, "id");
    try {
      const r = await apiFetch(`${API_URL}/api/price-contracts/${id}/customers`, {
        method: "POST",
        body: JSON.stringify({
          custNum: custNumInt,
          custId: custId || null,
          customerName: customerName || null,
        }),
      });
      const body = await r.json().catch(() => ({}));   // ONE read, before any branch
      if (r.status === 409) {
        // ContractConflictDto: { error, conflictingContract, current }. The
        // server's sentence already names the other contract; the fallback
        // below only exists so this never degrades into the word "conflict".
        const other = f(body, "conflictingContract", null) || null;
        const fallback = other
          ? `${custId || who} is already under ${label(other)} until ${fmtDay(f(other, "contractEndDate"))}. Detach them from that contract first.`
          : `${custId || who} is already on another price contract covering those dates.`;
        return { error: humanError(r, body, fallback), conflicting: other };
      }
      if (!r.ok) return { error: humanError(r, body, `Couldn't attach ${who} to ${label(contract)}.`) };
      applyRow(body);
      setNotice({ kind: "ok", msg: `${who} is now attached to ${label(body)}.` });
      await load(true);
      return { ok: true };
    } catch (e) {
      return { error: errorText(e, `Couldn't attach ${who} to ${label(contract)}.`) };
    }
  };

  const detach = async (contract) => {
    const id = f(contract, "id");
    const live = f(contract, "isLiveNow", false) === true;
    const confirmText = [
      `Detach ${who} from ${label(contract)}?`,
      live
        ? "This is the contract pricing their quotes today. New quotes will be priced from the catalogue unless another attached contract covers them."
        : "New quotes will be priced from the catalogue unless another attached contract covers them.",
      "The detach is recorded against the contract, not deleted.",
    ].join("\n\n");
    if (!window.confirm(confirmText)) return;

    setActing(true);
    setNotice(null);
    try {
      const r = await apiFetch(`${API_URL}/api/price-contracts/${id}/customers/${custNumInt}`, { method: "DELETE" });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(humanError(r, body, `Couldn't detach ${who} from ${label(contract)}.`));
      applyRow(body);
      setNotice({ kind: "ok", msg: `${who} detached from ${label(body)}.` });
      await load(true);
    } catch (e) {
      setNotice({ kind: "err", msg: errorText(e, `Couldn't detach ${who} from ${label(contract)}.`) });
    } finally {
      setActing(false);
    }
  };

  // The contracts admin page is a sibling screen; the query param is a hint it
  // may use to open straight onto the row, and is harmless if it ignores it.
  const openAdmin = (contract) =>
    navigate(contract ? `/price-contracts?contractId=${f(contract, "id")}` : "/price-contracts");

  // ── no permission ───────────────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="qw-card qw-pad">
        <div className="qw-eyebrow">Contract pricing</div>
        <Info tone="qw-info-amber" style={{ marginTop: 10 }}>
          You don't have permission to view price contracts. Ask an administrator for the
          price-contract view entitlement if you need to check how this customer is priced.
        </Info>
      </div>
    );
  }

  // The tab is mounted from the customer page, which always has a custNum —
  // but a blank panel with no explanation is the one thing this screen must
  // never do, so say why instead of firing /api/customers/NaN/price-contract.
  if (!Number.isFinite(custNumInt)) {
    return (
      <div className="qw-card qw-pad">
        <div className="qw-eyebrow">Contract pricing</div>
        <Info tone="qw-info-amber" style={{ marginTop: 10 }}>
          No customer number was passed to this tab, so contract pricing can't be looked up.
          Reopen the customer from the list and try again.
        </Info>
      </div>
    );
  }

  const governing = panel && panel.governing;
  // The headline is PricingHeadline's job now — it reads pricingMode, which is the only
  // field that knows about the runtime switches. A local isLiveNow flag here would be a
  // second, blunter opinion about the same question, and the two would drift.
  const attached = (panel && panel.attached) || [];
  const others = attached.filter((c) => !governing || f(c, "id") !== f(governing, "id"));
  const busy = acting || refreshing;

  return (
    <div className="qw-stack">
      {/* ── the answer ─────────────────────────────────────────────────────── */}
      <div className="qw-card qw-pad">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <div className="qw-eyebrow">Contract pricing</div>
            <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>
              How this customer's quotes are priced — from a signed contract's price book, or from the catalogue.
            </p>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {canManage && (
              <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={busy}
                onClick={() => setCreateOpen(true)}>
                + New contract for {custId || "this customer"}
              </button>
            )}
            <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={() => openAdmin(null)}>
              Contracts admin →
            </button>
          </div>
        </div>

        {loading && (
          <div style={{ color: "#64748B", padding: "16px 0" }}>
            <Spinner animation="border" size="sm" /> Loading contract pricing…
          </div>
        )}
        {error && (
          <Info tone="qw-info-amber" style={{ marginTop: 10, background: "#FEF2F2", color: "#991B1B" }}>
            {error}
            <div style={{ marginTop: 8 }}>
              <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={() => load()}>Try again</button>
            </div>
          </Info>
        )}
        {notice && (
          <Info
            style={{
              marginTop: 10,
              background: notice.kind === "err" ? "#FEF2F2" : "#ECFDF5",
              color: notice.kind === "err" ? "#991B1B" : "#065F46",
            }}
          >
            {notice.msg}
          </Info>
        )}

        {!loading && !error && panel && (
          governing ? (
            <div style={{ marginTop: 14 }}>
              {/* THE HEADLINE COMES FROM pricingMode, NOT FROM isLiveNow ALONE.
                  A binary live/not-live reads every non-live state as something being WRONG
                  with the contract. During a shadow run that is actively misleading: the
                  contract is perfect, the engine is deliberately not writing prices, and
                  "this contract does NOT price their quotes" sends someone to check a
                  contract that has nothing wrong with it.
                  Four states, four different things to do about them. */}
              <PricingHeadline c={governing} who={who} />
              <div className="qw-card-flat qw-pad" style={{ marginTop: 12 }}>
                <ContractHead c={governing} />
                <ContractFacts c={governing} />
                <ApprovalLine c={governing} />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                  <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={() => openAdmin(governing)}>
                    Open in contracts admin
                  </button>
                  {canManage && (
                    <button
                      className="qw-btn qw-btn-danger qw-btn-sm"
                      disabled={busy}
                      onClick={() => detach(governing)}
                    >
                      Detach from this contract
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <Info tone="qw-info-indigo">
                <strong>{who} is not on a price contract today.</strong> Their quotes are priced from the
                catalogue — the same prices as any other customer.
                {attached.length > 0 && (
                  <>{" "}
                  {attached.length === 1
                    ? "One contract is attached to this customer, but it isn't pricing quotes today."
                    : `${attached.length} contracts are attached to this customer, but none of them are pricing quotes today.`}
                  {" "}The reason is on each one below.</>
                )}
              </Info>
            </div>
          )
        )}
      </div>

      {/* ── everything attached ────────────────────────────────────────────── */}
      {!loading && !error && panel && (
        <div className="qw-card qw-pad">
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div>
              <div className="qw-eyebrow">Attached contracts ({attached.length})</div>
              <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>
                Every contract this customer is attached to, not only the one pricing quotes today.
                More than one is normal — a renewal signed in November sits alongside this year's
                contract until its window opens.
              </p>
            </div>
            {canManage && (
              <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={busy} onClick={() => { setNotice(null); setPickerOpen(true); }}>
                ＋ Attach to a contract
              </button>
            )}
          </div>

          {refreshing && (
            <div style={{ color: "#64748B", fontSize: 12, marginTop: 8 }}>
              <Spinner animation="border" size="sm" /> Refreshing…
            </div>
          )}

          {attached.length === 0 ? (
            <div style={{ color: "#94A3B8", fontSize: 13, marginTop: 12 }}>
              No contracts are attached to this customer.
              {canManage
                ? " Attach one when a signed contract covers them; until then their quotes use catalogue pricing."
                : " Their quotes use catalogue pricing."}
            </div>
          ) : (
            <div className="qw-stack" style={{ marginTop: 12 }}>
              {governing && (
                <AttachedCard
                  c={governing}
                  isGoverning
                  canManage={canManage}
                  busy={busy}
                  onOpen={() => openAdmin(governing)}
                  onDetach={() => detach(governing)}
                />
              )}
              {others.map((c, i) => (
                <AttachedCard
                  key={f(c, "id") || i}
                  c={c}
                  isGoverning={false}
                  canManage={canManage}
                  busy={busy}
                  onOpen={() => openAdmin(c)}
                  onDetach={() => detach(c)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <ContractFormModal
          // Pre-filled with the customer the user is already looking at. signingCustNum and
          // signingEntityName are the contract's OWN signing party, which for a single-hospital
          // contract is this customer — and for a group contract is the first thing they will
          // change. A wrong-but-visible default gets corrected; an empty field gets skipped.
          initial={{
            contractName: customerName ? `${customerName} price contract` : "",
            signingEntityName: customerName || "",
            signingCustNum: custNumInt || "",
          }}
          saving={creating}
          onClose={() => setCreateOpen(false)}
          onSubmit={createAndAttach}
        />
      )}

      {pickerOpen && (
        <AttachContractModal
          who={who}
          attachedIds={attached.map((c) => f(c, "id"))}
          onAttach={attach}
          onClose={() => setPickerOpen(false)}
          onOpenAdmin={openAdmin}
        />
      )}
    </div>
  );
}

// =============================================================================
// Pieces
// =============================================================================
function ContractHead({ c }) {
  const live = f(c, "isLiveNow", false) === true;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span className="qw-mono" style={{ fontWeight: 700, color: "#0F172A" }}>{f(c, "contractRef") || "(no ref)"}</span>
        <span style={{ fontWeight: 700, color: "#0F172A" }}>{f(c, "contractName") || "—"}</span>
        <span className={"qw-pill " + statusPill(f(c, "status"))}>{statusWord(f(c, "status"))}</span>
        {live && <span className="qw-pill qw-pill-indigo">LIVE NOW</span>}
        {f(c, "isEnabled", true) === false && <span className="qw-pill qw-pill-amber">SWITCHED OFF</span>}
      </div>
      <div style={{ fontSize: 11, color: "#94A3B8" }}>
        {f(c, "customerCount", 0)} customer(s) on this contract
      </div>
    </div>
  );
}

function ContractFacts({ c }) {
  const year = f(c, "referencePriceYear", null);
  const words = pctWords(f(c, "adjustPercent"), year);
  return (
    <>
      <Info tone="qw-info-indigo" style={{ marginTop: 10, fontSize: 13 }}>
        <strong>{f(c, "effectDescription") || "—"}</strong>
        {words && (
          <div style={{ marginTop: 4 }}>
            Adjustment <span className="qw-mono" style={{ fontWeight: 700 }}>{pctSigned(f(c, "adjustPercent"))}</span> — the
            customer pays <strong>{words}</strong>.
          </div>
        )}
      </Info>
      <div style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>
        Runs {fmtDay(f(c, "contractStartDate"))} → {fmtDay(f(c, "contractEndDate"))}
        {year ? ` · prices taken from the ${year} book` : ""}
        {f(c, "signingEntityName") ? ` · signed by ${f(c, "signingEntityName")}` : ""}
      </div>
      {f(c, "notes") && (
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 6 }}>{f(c, "notes")}</div>
      )}
    </>
  );
}

function ApprovalLine({ c }) {
  const approvedBy = f(c, "approvedBy");
  const recordedBy = f(c, "approvalRecordedBy");
  const disabledBy = f(c, "disabledBy");
  const reason = f(c, "disableReason");
  return (
    <div style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>
      <div>
        Created by {f(c, "createdBy") || "—"} · {fmtStamp(f(c, "createdAt"))}
      </div>
      {approvedBy ? (
        <div>
          Approved by {approvedBy} · {fmtStamp(f(c, "approvedAt"))}
          {recordedBy ? ` · keyed in by ${recordedBy}` : ""}
          {f(c, "approvalNote") ? ` · ${f(c, "approvalNote")}` : ""}
        </div>
      ) : (
        <div>Not approved yet{f(c, "submittedBy") ? ` · submitted by ${f(c, "submittedBy")} on ${fmtStamp(f(c, "submittedAt"))}` : ""}</div>
      )}
      {disabledBy && (
        <div style={{ color: "#B91C1C" }}>
          Switched off by {disabledBy} · {fmtStamp(f(c, "disabledAt"))}{reason ? ` · ${reason}` : ""}
        </div>
      )}
    </div>
  );
}

// The governing contract appears in attached[] too, so it is listed here as
// well — the count and the list have to agree, or the panel looks like it is
// hiding something. It is listed in short form, since its detail and its
// Detach button are directly above.
function AttachedCard({ c, isGoverning, canManage, busy, onOpen, onDetach }) {
  const live = f(c, "isLiveNow", false) === true;
  return (
    <div
      className="qw-card-flat qw-pad"
      style={isGoverning ? { borderColor: "#A5B4FC", background: "#FBFBFF" } : undefined}
    >
      <ContractHead c={c} />
      <div style={{ fontSize: 13, marginTop: 8, color: live ? "#065F46" : "#92400E", fontWeight: 600 }}>
        {live
          ? "Prices this customer's quotes today."
          : `Does NOT price this customer's quotes today — ${whyNotLive(c)}.`}
      </div>
      {isGoverning ? (
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 6 }}>
          Shown in full at the top of this tab, with its Detach button.
        </div>
      ) : (
        <>
          <ContractFacts c={c} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
            <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={onOpen}>Open in contracts admin</button>
            {canManage && (
              <button className="qw-btn qw-btn-danger qw-btn-sm" disabled={busy} onClick={onDetach}>Detach</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Attach picker — pick an existing contract, POST this customer onto it.
//
// The list endpoint filters by status/year/custNum only; there is no text
// search, so the box below narrows the page that is on screen and says so.
// =============================================================================
function AttachContractModal({ who, attachedIds, onAttach, onClose, onOpenAdmin }) {
  const [rows, setRows] = useState(null);          // null = not loaded yet
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attachingId, setAttachingId] = useState(null);
  const [refusal, setRefusal] = useState(null);    // { msg, conflicting }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const qs = new URLSearchParams({ page: String(page), pageSize: String(PICKER_PAGE_SIZE) });
        if (status) qs.set("status", status);
        const r = await apiFetch(`${API_URL}/api/price-contracts?${qs.toString()}`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't load the list of contracts."));
        if (!cancelled) {
          setRows(f(d, "items", []) || []);
          setTotalCount(Number(f(d, "totalCount", 0)) || 0);
        }
      } catch (e) {
        if (!cancelled) { setError(errorText(e, "Couldn't load the list of contracts.")); setRows([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, status]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q || !rows) return rows || [];
    return rows.filter((c) =>
      [f(c, "contractRef"), f(c, "contractName"), f(c, "signingEntityName"), f(c, "effectDescription")]
        .some((v) => String(v || "").toLowerCase().includes(q)));
  }, [rows, filter]);

  const doAttach = async (c) => {
    setAttachingId(f(c, "id"));
    setRefusal(null);
    const res = await onAttach(c);
    setAttachingId(null);
    if (res && res.ok) { onClose(); return; }
    setRefusal({ msg: (res && res.error) || "That didn't go through. Please try again.", conflicting: (res && res.conflicting) || null });
  };

  const busy = attachingId != null;
  const from = totalCount === 0 ? 0 : (page - 1) * PICKER_PAGE_SIZE + 1;
  const to = (page - 1) * PICKER_PAGE_SIZE + ((rows && rows.length) || 0);

  return (
    <Modal show onHide={busy ? undefined : onClose} centered scrollable size="lg" contentClassName="qw-root">
      <Modal.Header closeButton={!busy}>
        <Modal.Title style={{ fontSize: 18 }}>Attach {who} to a contract</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="qw-sub" style={{ fontSize: 12, marginTop: 0 }}>
          Pick the signed contract this customer is covered by. Attaching is safe to repeat, and
          re-attaching somebody who was detached earlier brings their original row back.
        </p>

        {refusal && (
          <Info tone="qw-info-amber" style={{ marginBottom: 12, background: "#FEF2F2", color: "#991B1B" }}>
            <div>{refusal.msg}</div>
            {refusal.conflicting && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#7F1D1D" }}>
                <span className="qw-mono" style={{ fontWeight: 700 }}>{label(refusal.conflicting)}</span>
                {" — "}{f(refusal.conflicting, "contractName") || "—"}
                {" · "}{fmtDay(f(refusal.conflicting, "contractStartDate"))} → {fmtDay(f(refusal.conflicting, "contractEndDate"))}
                {" · "}{f(refusal.conflicting, "effectDescription") || "—"}
                <div style={{ marginTop: 6 }}>
                  <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={() => onOpenAdmin(refusal.conflicting)}>
                    Open that contract
                  </button>
                </div>
              </div>
            )}
          </Info>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ flex: "1 1 180px", minWidth: 0 }}>
            <label className="qw-label" htmlFor="pc-status">Status</label>
            <select
              id="pc-status"
              className="qw-input"
              value={status}
              disabled={busy}
              onChange={(e) => { setPage(1); setStatus(e.target.value); }}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s === "PendingApproval" ? "Pending approval" : s}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "2 1 220px", minWidth: 0 }}>
            <label className="qw-label" htmlFor="pc-filter">Narrow this page</label>
            <input
              id="pc-filter"
              className="qw-input"
              placeholder="ref, name or signing entity…"
              value={filter}
              disabled={busy}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 24, color: "#64748B" }}>
            <Spinner animation="border" size="sm" /> Loading contracts…
          </div>
        )}
        {error && (
          <Info tone="qw-info-amber" style={{ background: "#FEF2F2", color: "#991B1B" }}>{error}</Info>
        )}
        {!loading && !error && rows && rows.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: "#94A3B8", fontSize: 13 }}>
            {status ? "No contracts with that status." : "No contracts exist yet. Create one in the contracts admin screen first."}
          </div>
        )}
        {!loading && !error && rows && rows.length > 0 && visible.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: "#94A3B8", fontSize: 13 }}>
            Nothing on this page matches "{filter}".
          </div>
        )}

        {!loading && !error && visible.length > 0 && (
          <div className="qw-stack">
            {visible.map((c, i) => {
              const id = f(c, "id");
              const already = attachedIds.includes(id);
              return (
                <div key={id || i} className="qw-card-flat qw-pad">
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span className="qw-mono" style={{ fontWeight: 700, color: "#0F172A" }}>{f(c, "contractRef") || "(no ref)"}</span>
                      <span style={{ fontWeight: 700, color: "#0F172A" }}>{f(c, "contractName") || "—"}</span>
                      <span className={"qw-pill " + statusPill(f(c, "status"))}>{statusWord(f(c, "status"))}</span>
                      {f(c, "isLiveNow", false) === true && <span className="qw-pill qw-pill-indigo">LIVE NOW</span>}
                      {already && <span className="qw-pill qw-pill-violet">ALREADY ATTACHED</span>}
                    </div>
                    <button
                      className="qw-btn qw-btn-primary qw-btn-sm"
                      disabled={busy}
                      onClick={() => doAttach(c)}
                    >
                      {attachingId === id ? <><Spinner animation="border" size="sm" style={{ marginRight: 6 }} /> Attaching…</> : "Attach"}
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 6 }}>
                    {f(c, "effectDescription") || "—"} · adjustment{" "}
                    <span className="qw-mono" style={{ fontWeight: 700 }}>{pctSigned(f(c, "adjustPercent"))}</span>
                    {" "}({pctWords(f(c, "adjustPercent"), f(c, "referencePriceYear", null))})
                  </div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                    {fmtDay(f(c, "contractStartDate"))} → {fmtDay(f(c, "contractEndDate"))} · {f(c, "customerCount", 0)} customer(s)
                    {f(c, "signingEntityName") ? ` · ${f(c, "signingEntityName")}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 12, color: "#64748B" }}>
          {totalCount > 0 ? `Showing ${from}–${to} of ${totalCount}` : ""}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy || loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
          <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy || loading || to >= totalCount} onClick={() => setPage((p) => p + 1)}>Next →</button>
          <button className="qw-btn qw-btn-ghost" disabled={busy} onClick={onClose}>Close</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
